import React, { useState } from 'react';

interface Card {
  front: string;
  back: string;
  pronunciation?: string;
  example?: string;
}

interface FlashcardExerciseProps {
  cards: Card[];
  onComplete: (answer: any) => void;
}

/**
 * FlashcardExercise Component
 *
 * Displays flashcards that users can flip to study vocabulary.
 */
const FlashcardExercise: React.FC<FlashcardExerciseProps> = ({ cards, onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [studiedCards, setStudiedCards] = useState<Set<number>>(new Set());

  const currentCard = cards[currentIndex];
  const progress = ((currentIndex + 1) / cards.length) * 100;

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
    if (!isFlipped) {
      const newSet = new Set(studiedCards);
      newSet.add(currentIndex);
      setStudiedCards(newSet);
    }
  };

  const handleNext = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleComplete = () => {
    onComplete({ studied_cards: studiedCards.size, total_cards: cards.length });
  };

  const isLastCard = currentIndex === cards.length - 1;
  const allStudied = studiedCards.size === cards.length;

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Progress */}
      <div>
        <div className="flex justify-between text-sm mb-2">
          <span>Card {currentIndex + 1} of {cards.length}</span>
          <span>{studiedCards.size} studied</span>
        </div>
        <progress className="progress progress-primary" value={progress} max="100"></progress>
      </div>

      {/* Flashcard */}
      <div
        className="relative w-full h-80 cursor-pointer perspective-1000"
        onClick={handleFlip}
      >
        <div
          className={`
            relative w-full h-full transition-transform duration-500 transform-style-3d
            ${isFlipped ? 'rotate-y-180' : ''}
          `}
          style={{
            transformStyle: 'preserve-3d',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
          }}
        >
          {/* Front */}
          <div
            className="absolute inset-0 backface-hidden"
            style={{ backfaceVisibility: 'hidden' }}
          >
            <div className="card bg-primary text-primary-content h-full shadow-xl">
              <div className="card-body items-center justify-center text-center">
                <h2 className="card-title text-4xl mb-4">{currentCard.front}</h2>
                <p className="text-sm opacity-70">Click to flip</p>
              </div>
            </div>
          </div>

          {/* Back */}
          <div
            className="absolute inset-0 backface-hidden"
            style={{
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)'
            }}
          >
            <div className="card bg-secondary text-secondary-content h-full shadow-xl">
              <div className="card-body justify-center">
                <h2 className="card-title text-3xl mb-2">{currentCard.back}</h2>

                {currentCard.pronunciation && (
                  <p className="text-lg opacity-80 mb-4">{currentCard.pronunciation}</p>
                )}

                {currentCard.example && (
                  <div className="mt-4 p-4 bg-base-100/20 rounded-lg">
                    <p className="text-sm italic">{currentCard.example}</p>
                  </div>
                )}

                <p className="text-sm opacity-70 mt-4">Click to flip back</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePrevious();
          }}
          className="btn btn-outline"
          disabled={currentIndex === 0}
        >
          ← Previous
        </button>

        {!isLastCard ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
            className="btn btn-primary"
          >
            Next →
          </button>
        ) : (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleComplete();
            }}
            className="btn btn-success"
            disabled={!allStudied}
          >
            {allStudied ? 'Complete ✓' : 'Study all cards first'}
          </button>
        )}
      </div>

      {/* Card Grid Navigation */}
      <div className="divider">All Cards</div>
      <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
        {cards.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setCurrentIndex(index);
              setIsFlipped(false);
            }}
            className={`
              btn btn-sm btn-square
              ${index === currentIndex ? 'btn-primary' :
                studiedCards.has(index) ? 'btn-success' :
                'btn-outline'}
            `}
          >
            {index + 1}
          </button>
        ))}
      </div>
    </div>
  );
};

export default FlashcardExercise;
